import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';

export default function EjecutivoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // { ejecutivo, meses, series, kpis6m, ... }
  const token = () => localStorage.getItem("token");

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      window.location.href = "/login";
      return;
    }
    axios
      .get(`https://api.t-sales.cl/api/ejecutivos/${id}/analitica`, {
        headers: { Authorization: `Bearer ${token()}` },
      })
      .then((res) => setData(res.data))
      .catch((err) => console.error("Error analítica:", err))
      .finally(() => setLoading(false));
  }, [id]);

  // Dataset por mes (12 meses)
  const chartData = useMemo(() => {
    if (!data) return [];
    const meses = data.meses || [];
    const sf = data.series?.fijo || {};
    const sm = data.series?.movil || {};
    const ft = sf.total || [];
    const fp = sf.penalizadas || [];
    const mt = sm.total || [];
    const mp = sm.penalizadas || [];
    return meses.map((m, i) => ({
      periodo: m,
      fijo_total: ft[i] ?? 0,
      fijo_pen: fp[i] ?? 0,
      movil_total: mt[i] ?? 0,
      movil_pen: mp[i] ?? 0,
    }));
  }, [data]);

  // Helper para N3/N6 (excluir SOLO el último período cargado = M0)
  // Helper para N3/N6: usa como M0 el último periodo con datos (>0) y lo EXCLUYE.
  const buildPenRows = (channel, monthsCount) => {
    const totalKey = channel === "fijo" ? "fijo_total" : "movil_total";
    const penKey   = channel === "fijo" ? "fijo_pen"   : "movil_pen";

    const len = chartData.length;
    if (len === 0) return [];

    // Buscar desde el final el último periodo con datos en ese canal
    let lastIdxWithData = -1;
    for (let i = len - 1; i >= 0; i--) {
      const t = Number(chartData[i]?.[totalKey] || 0);
      const p = Number(chartData[i]?.[penKey] || 0);
      if (t > 0 || p > 0) { lastIdxWithData = i; break; }
    }
    if (lastIdxWithData < 0) return []; // no hay data en todo el rango

    // Excluir M0 (lastIdxWithData) y tomar los N meses anteriores
    const endExcl = lastIdxWithData;                 // excluye M0
    const start   = Math.max(0, endExcl - monthsCount);
    return chartData.slice(start, endExcl).map(row => {
      const total = Number(row[totalKey] || 0);
      const pen   = Math.min(Number(row[penKey] || 0), total);
      const pct   = total > 0 ? (pen * 100 / total) : 0;
      return [row.periodo, total, pen, `${pct.toFixed(1)}%`];
    });
  };


  const rowsFijo3  = useMemo(() => buildPenRows("fijo", 3),  [chartData]);
  const rowsMovil3 = useMemo(() => buildPenRows("movil", 3), [chartData]);
  const rowsFijo6  = useMemo(() => buildPenRows("fijo", 6),  [chartData]);
  const rowsMovil6 = useMemo(() => buildPenRows("movil", 6), [chartData]);

  const chartsToShow = useMemo(() => {
    if (!data) return [];
    const canal = (data.ejecutivo?.canal || "").toLowerCase();

    if (canal.includes("pyme movil")) return ["fijo", "movil", "sspp"];
    if (canal.includes("masivo")) return ["fijo", "movil"];
    if (canal.includes("pyme fijo")) return ["fijo"];
    if (canal.includes("sspp")) return ["sspp"];

    const sumKey = (k) => chartData.reduce((a, r) => a + (r[k] || 0), 0);
    const out = [];
    if (sumKey("fijo_total") > 0) out.push("fijo");
    if (sumKey("movil_total") > 0) out.push("movil");
    return out.length ? out : ["fijo"];
  }, [data, chartData]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-700">Cargando...</div>;
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-red-600">No hay datos</div>;
  }

  const ej = data.ejecutivo || {};
  const kpis6m = data.kpis6m || {};
  const ventas = data.ventas || [];

  return (
    <div className="min-h-screen bg-[#f7fafb] text-[#0F1C17] px-4 py-8">
      <div className="max-w-7xl mx-auto">
        <header className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Análisis de Ejecutivo</h1>
            <p className="text-sm text-gray-600">Detalle y desempeño histórico</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="rounded-xl px-4 py-2 bg-[#006f91] text-white hover:opacity-90"
          >
            ← Volver
          </button>
        </header>

        {/* Resumen + KPIs 6M */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card>
            <h3 className="font-semibold text-lg mb-3">🧍 Resumen</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Field k="Nombre" v={ej.nombre} />
              <Field k="RUT" v={ej.rut} />
              <Field k="Correo" v={ej.correo || "—"} />
              <Field k="Canal" v={ej.canal || "—"} />
              <Field k="Activo" v={ej.activo ? "Sí" : "—"} />
              <Field k="Fecha inicio" v={ej.fecha_inicio || "—"} />
            </div>
          </Card>

          <Card>
            <h3 className="font-semibold text-lg mb-3">📊 KPIs últimos 6 meses (Global)</h3>
            <div className="grid grid-cols-3 gap-3">
              <Kpi label="Ventas" value={kpis6m.global?.total ?? 0} />
              <Kpi label="Penalizadas" value={kpis6m.global?.penalizadas ?? 0} />
              <Kpi label="Tasa penalización" value={`${kpis6m.global?.tasa ?? 0}%`} />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <SubKpi title="Fijo" data={kpis6m.fijo} />
              <SubKpi title="Móvil" data={kpis6m.movil} />
            </div>
          </Card>
        </section>

        {/* Gráficos */}
        <section className="space-y-6 mb-8">
          {chartsToShow.includes("fijo") && (
            <ChartBlockDual
              title="Fijo – Ventas totales (azul) con tramo penalizado (rojo)"
              data={chartData}
              totalKey="fijo_total"
              penKey="fijo_pen"
            />
          )}
          {chartsToShow.includes("movil") && (
            <ChartBlockDual
              title="Móvil – Ventas totales (azul) con tramo penalizado (rojo)"
              data={chartData}
              totalKey="movil_total"
              penKey="movil_pen"
            />
          )}
        </section>

        {/* Tablas N3/N6 */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Card>
            <h3 className="font-semibold text-lg mb-3">📍 Penalizaciones Fijo 3M</h3>
            <SimpleTable
              headers={["Periodo", "Cantidad", "Penalizadas", "% Penalizadas"]}
              rows={rowsFijo3}
              emptyText="Sin datos en la ventana N3"
            />
          </Card>
          <Card>
            <h3 className="font-semibold text-lg mb-3">📍 Penalizaciones Móvil 3M</h3>
            <SimpleTable
              headers={["Periodo", "Cantidad", "Penalizadas", "% Penalizadas"]}
              rows={rowsMovil3}
              emptyText="Sin datos en la ventana N3"
            />
          </Card>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
          <Card>
            <h3 className="font-semibold text-lg mb-3">📍 Penalizaciones Fijo 6M</h3>
            <SimpleTable
              headers={["Periodo", "Cantidad", "Penalizadas", "% Penalizadas"]}
              rows={rowsFijo6}
              emptyText="Sin datos en la ventana N6"
            />
          </Card>
          <Card>
            <h3 className="font-semibold text-lg mb-3">📍 Penalizaciones Móvil 6M</h3>
            <SimpleTable
              headers={["Periodo", "Cantidad", "Penalizadas", "% Penalizadas"]}
              rows={rowsMovil6}
              emptyText="Sin datos en la ventana N6"
            />
          </Card>
        </section>

        {/* Historia de ventas */}
        <section className="mb-12">
          <Card>
            <h3 className="font-semibold text-lg mb-4">🧾 Historia de ventas (todas las del rango)</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left bg-gray-100">
                    <Th>Fecha</Th>
                    <Th>Tipo</Th>
                    <Th>ID/Orden</Th>
                    <Th>Cliente</Th>
                    <Th>Producto</Th>
                    <Th>Estado</Th>
                    <Th>Periodo</Th>
                  </tr>
                </thead>
                <tbody>
                  {ventas.length === 0 ? (
                    <tr><td className="py-8 text-center text-gray-500" colSpan={7}>Sin ventas</td></tr>
                  ) : (
                    ventas.map((v) => (
                      <tr key={`${v.tipo}-${v.id}`} className="border-b last:border-b-0 hover:bg-gray-50">
                        <Td>{v.fecha || "—"}</Td>
                        <Td>{v.tipo}</Td>
                        <Td>{v.id_generico || v.orden || "—"}</Td>
                        <Td>{v.cliente || "—"}</Td>
                        <Td>{v.producto || "—"}</Td>
                        <Td>{v.estado || "—"}</Td>
                        <Td>{v.periodo || "—"}</Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
      </div>
    </div>
  );
}

/* ===== UI helpers ===== */
function Card({ children }) {
  return (
    <motion.div
      className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      {children}
    </motion.div>
  );
}
function Field({ k, v }) {
  return (
    <div className="flex flex-col">
      <span className="text-[11px] uppercase tracking-wide text-gray-500">{k}</span>
      <span className="font-medium break-all">{v}</span>
    </div>
  );
}
function Kpi({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <div className="text-xs text-gray-600 mb-1">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}
function SubKpi({ title, data }) {
  const total = data?.total ?? 0;
  const pen = data?.penalizadas ?? 0;
  const tasa = data?.tasa ?? 0;
  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <div className="text-sm font-semibold mb-2">{title}</div>
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div><div className="text-gray-500">Ventas</div><div className="font-semibold">{total}</div></div>
        <div><div className="text-gray-500">Penalizadas</div><div className="font-semibold">{pen}</div></div>
        <div><div className="text-gray-500">Tasa</div><div className="font-semibold">{tasa}%</div></div>
      </div>
    </div>
  );
}
function SimpleTable({ headers, rows, emptyText }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left bg-gray-100">
            {headers.map((h) => (
              <th key={h} className="px-3 py-2 text-xs font-semibold text-gray-600 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(!rows || rows.length === 0) ? (
            <tr><td className="py-8 text-center text-gray-500" colSpan={headers.length}>{emptyText}</td></tr>
          ) : (
            rows.map((r, idx) => (
              <tr key={idx} className="border-b last:border-b-0 hover:bg-gray-50">
                {r.map((cell, i) => <td key={i} className="px-3 py-2 whitespace-nowrap">{cell}</td>)}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
function Th({ children }) {
  return <th className="px-3 py-2 text-xs font-semibold text-gray-600">{children}</th>;
}
function Td({ children }) {
  return <td className="px-3 py-2">{children}</td>;
}

/* === CHART: ALTURA = VENTAS TOTALES.
   Tramo rojo = porcentaje penalizado dentro de la barra. === */
function ChartBlockDual({ title, data, totalKey, penKey }) {
  const rows = React.useMemo(() => {
    return (data || []).map((d) => {
      const gross = Math.max(0, Number(d?.[totalKey] ?? 0)); // ventas totales del periodo
      const pen   = Math.max(0, Number(d?.[penKey] ?? 0));
      const ratio = gross > 0 ? Math.min(pen / gross, 1) : 0; // % penalizado del total
      return { ...d, _base: gross, _pen: pen, _ratio: ratio, _gross: gross };
    });
  }, [data, totalKey, penKey]);

  // Dibuja azul (altura = total) + franja roja proporcional dentro
  const SegmentedBar = (props) => {
    const { x, y, width, height, payload } = props;
    const ratio = Math.max(0, Math.min(Number(payload?._ratio ?? 0), 1));
    const redH  = height * ratio;           // alto rojo = % penalizado del total
    const redY  = y + (height - redH);
    return (
      <g>
        <rect x={x} y={y} width={width} height={height} fill="#2563eb" rx="6" ry="6" />
        {redH > 0 && (
          <rect x={x} y={redY} width={width} height={redH} fill="#ef4444" rx="0" ry="0" />
        )}
      </g>
    );
  };

  const Tip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded px-2 py-1 text-xs">
        <div className="font-semibold mb-1">{label}</div>
        <div>Ventas totales: {p._gross}</div>
        <div>Penalizadas: {p._pen}</div>
      </div>
    );
  };

  const TopLabel = (props) => {
    const { x, y, width, value } = props;
    if (value == null) return null;
    return (
      <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize="12" fill="#111827">
        {value}
      </text>
    );
  };

  return (
    <Card>
      <h3 className="font-semibold text-lg mb-3">{title}</h3>
      <div style={{ width: "100%", height: 320 }}>
        <ResponsiveContainer>
          <BarChart data={rows} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="periodo" />
            <YAxis allowDecimals={false} domain={[0, 'dataMax']} />
            <Tooltip content={<Tip />} />
            <Legend
              payload={[
                { id: 'tot', value: 'Ventas totales (altura)', type: 'square', color: '#2563eb' },
                { id: 'pen', value: 'Penalizadas (tramo rojo)', type: 'square', color: '#ef4444' },
              ]}
            />
            {/* Altura = total; el shape pinta la franja roja dentro */}
            <Bar dataKey="_base" name="Ventas totales" fill="transparent" shape={SegmentedBar} isAnimationActive={false}>
              <LabelList dataKey="_base" content={<TopLabel />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
